import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors } from '@angular/forms';
import { FormGroup, Validators } from '@angular/forms';
import { DataValidationService } from '@app/util/data-validation.service';
import * as moment from 'moment';
import { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { appConstants } from '@app/common/app-constants';
import { AppConfigService } from '@app/common/app-config.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-search-patient',
  templateUrl: './search-patient.component.html',
  styleUrls: ['./search-patient.component.scss']
})
export class SearchPatientComponent {
  @Output() searchPatient = new EventEmitter<string>();
  @Input() searchFormValue;

  form: FormGroup;
  subscriptions: Array<Subscription> = [];
  genderOptions = [
    this.translate.instant('dicomqr.selectPatientSexLabel.male'),
    this.translate.instant('dicomqr.selectPatientSexLabel.female')
  ];

  patientDateOfBirth: Moment;
  patientGender: string;
  isBackButtonTriggered = false;
  dateTimePref = {} as any;

  constructor(
    private fb: FormBuilder,
    private dataValidationService: DataValidationService,
    private appConfigService: AppConfigService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.addVisibilityValidation(appConstants.MergePatient.PID, appConstants.MergePatient.PATIENT_NAME);
    this.addVisibilityValidation(appConstants.MergePatient.PATIENT_NAME, appConstants.MergePatient.PID);
    if (this.searchFormValue) {
      this.isBackButtonTriggered = true;
      this.setFormValues();
    }
    this.dateTimePref = this.appConfigService.getDateTimePref();
  }

  buildForm() {
    this.form = this.fb.group({
      pid: ['', [Validators.required, Validators.maxLength(64), this.pidPrefixSuffixValidator.bind(this)]],
      patientName: ['', [Validators.required, Validators.maxLength(41), this.patientNameCustomValidator.bind(this)]],
      dob: [''],
      gender: ['']
    });
  }

  patientNameCustomValidator(control: AbstractControl): ValidationErrors | null {
    const patientNameValue = control.value;
    if (patientNameValue) {
      const index = patientNameValue.indexOf(',');
      const prefix = index > -1 && patientNameValue.substring(0, index).trim() <= 1;
      return this.dataValidationService.hasSqlInjectionChar(patientNameValue.trim()) ||
        prefix ||
        patientNameValue.trim().length < 2
        ? ({ patientNameValidation: true } as ValidationErrors)
        : null;
    }
    return null;
  }

  pidPrefixSuffixValidator(control: AbstractControl): ValidationErrors | null {
    const pidValue = control.value;
    if (pidValue) {
      const suffixTooLong = this.isSuffixTooLong(pidValue);
      const suffixTooShort = this.isSuffixTooShort(pidValue);
      const invalidPrefix = this.isInvalidPrefix(pidValue);

      const invalidSearchMRNCriteria =
        !!pidValue && (this.hasInvalidChar(pidValue, true) || suffixTooLong || invalidPrefix);
      return this.blockSearch(pidValue) || invalidSearchMRNCriteria || suffixTooShort
        ? ({ pidSuffixPrefixValidation: true } as ValidationErrors)
        : null;
    }
    return null;
  }

  blockSearch(searchCriteria) {
    return searchCriteria.trim().replace(/,/g, '').length <= 1;
  }

  isInvalidPrefix(searchCriteria) {
    return this.dataValidationService.isInvalidPrefix(searchCriteria, 3) && !!searchCriteria;
  }

  isSuffixTooShort(searchCriteria) {
    // Only MRN support suffix search
    const suffixLength = 4;
    return this.dataValidationService.isSuffixTooShort(searchCriteria, suffixLength) && !!searchCriteria;
  }

  isSuffixTooLong(searchCriteria) {
    // Only MRN support suffix search
    const suffixLength = 4;
    return this.dataValidationService.isSuffixTooLong(searchCriteria, suffixLength) && !!searchCriteria;
  }

  hasInvalidChar(searchCriteria, notAllowedSuffixSearch) {
    return (
      this.dataValidationService.hasSqlInjectionChar(searchCriteria) ||
      this.dataValidationService.hasInvalidWildcardsFormat(searchCriteria, notAllowedSuffixSearch)
    );
  }

  resetField(formField) {
    this.form.get(formField).patchValue('');
  }

  addVisibilityValidation(fieldToCheck: string, fieldToValidate: string) {
    this.subscriptions.push(
      this.form
        .get(fieldToCheck)
        .valueChanges.pipe(filter(v => v.length <= 1 || this.isBackButtonTriggered))
        .subscribe(value => {
          if (value.length > 0) {
            this.disableFormField(fieldToValidate);
          } else {
            this.enableFormField(fieldToValidate);
          }
        })
    );
  }

  get formControls() {
    return this.form.controls;
  }

  enableFormField(fieldName: string) {
    this.form.get(fieldName).enable({ onlySelf: true, emitEvent: false });
  }

  disableFormField(fieldName: string) {
    this.form.get(fieldName).disable({ onlySelf: true, emitEvent: false });
  }

  onSearch() {
    if (this.form.status === appConstants.MergePatient.FORM_VALID) {
      this.searchPatient.emit(this.form.value);
    }
  }

  setDob(dateOfBirth) {
    const date = dateOfBirth ? dateOfBirth.format('YYYY-MM-DD') : '';
    this.form.patchValue({ dob: date });
  }

  setDobError(error) {
    if (!error) {
      this.form.get(appConstants.MergePatient.DOB).updateValueAndValidity();
    } else {
      this.form.get(appConstants.MergePatient.DOB).setErrors({ invalidDob: true });
    }
  }

  setGender(selectedGender) {
    if (selectedGender) {
      this.form.patchValue({ gender: selectedGender.charAt(0) });
    } else {
      this.form.patchValue({ gender: '' });
    }
  }

  setFormValues() {
    Object.entries(this.searchFormValue).forEach(([key, value]) => {
      if (key === appConstants.MergePatient.DOB && value) {
        this.patientDateOfBirth = moment(value, 'YYYY-MM-DD');
        this.setDob(this.patientDateOfBirth);
      } else if (key === appConstants.MergePatient.Gender.LABEL && value) {
        this.patientGender =
          value === 'M' ? appConstants.MergePatient.Gender.MALE : appConstants.MergePatient.Gender.FEMALE;
        this.form.patchValue({ key: this.patientGender });
      } else {
        this.form.get(key).patchValue(value);
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
